import { getAmpliSessionId, waitForAmpliReady } from '../ampli';
import { safeLocalStorage, safeSessionStorage } from '../safeStorage';

export const campaignStorageKeys = {
	fallbackSessionId: 'proactive_campaigns_fallback_session_id',
	lock: 'proactive_campaigns_lock',
	snooze: 'proactive_campaigns_snooze',
} as const;

// Once a session started on the generated fallback id it stays on it — switching
// to a later-arriving Amplitude id mid-session would reset the mutex/snooze scope.
const getPinnedFallbackSessionId = (): string => {
	const existing = safeSessionStorage.getItem(campaignStorageKeys.fallbackSessionId);
	if (existing) {
		return existing;
	}
	const generated = `fallback-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
	safeSessionStorage.setItem(campaignStorageKeys.fallbackSessionId, generated);
	return safeSessionStorage.getItem(campaignStorageKeys.fallbackSessionId) ?? generated;
};

/**
 * Resolves the campaign session id: the Amplitude session (so campaign frequency
 * matches session-based analysis) when available, otherwise an id pinned to the
 * sessionStorage lifetime. Waits for the ampli SDK's readiness gate (bounded
 * internally) before deciding, so a session doesn't switch definitions mid-flight.
 */
export const resolveCampaignSessionId = async (): Promise<string> => {
	await waitForAmpliReady();
	const ampliSessionId = getAmpliSessionId();
	if (
		ampliSessionId !== undefined &&
		!safeSessionStorage.getItem(campaignStorageKeys.fallbackSessionId)
	) {
		return `ampli-${ampliSessionId}`;
	}
	return getPinnedFallbackSessionId();
};

interface SessionTaggedRecord {
	sessionId: string;
	campaignIds: string[];
}

const readSessionTagged = (key: string, sessionId: string): string[] => {
	const raw = safeLocalStorage.getItem(key);
	if (!raw) {
		return [];
	}
	try {
		const parsed = JSON.parse(raw) as SessionTaggedRecord;
		if (parsed?.sessionId !== sessionId || !Array.isArray(parsed.campaignIds)) {
			return [];
		}
		return parsed.campaignIds.filter((id): id is string => typeof id === 'string');
	} catch {
		return [];
	}
};

const appendSessionTagged = (key: string, sessionId: string, campaignId: string): void => {
	const existing = readSessionTagged(key, sessionId);
	if (existing.includes(campaignId)) {
		return;
	}
	const record: SessionTaggedRecord = { sessionId, campaignIds: [...existing, campaignId] };
	safeLocalStorage.setItem(key, JSON.stringify(record));
};

export const readSessionLock = (sessionId: string): string | null =>
	readSessionTagged(campaignStorageKeys.lock, sessionId)[0] ?? null;

export const writeSessionLock = (sessionId: string, campaignId: string): void => {
	appendSessionTagged(campaignStorageKeys.lock, sessionId, campaignId);
};

export const readSnoozedCampaignIds = (sessionId: string): string[] =>
	readSessionTagged(campaignStorageKeys.snooze, sessionId);

export const snoozeCampaign = (sessionId: string, campaignId: string): void => {
	appendSessionTagged(campaignStorageKeys.snooze, sessionId, campaignId);
};
